import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  User, 
  Mail, 
  Phone, 
  Trophy, 
  Star, 
  Clock, 
  Target, 
  Briefcase, 
  Wallet, 
  MapPin, 
  Calendar,
  CheckCircle2,
  XCircle,
  AlertCircle,
  TrendingUp,
  Users
} from "lucide-react";

const CAPACITE_LABELS = {
  moins_100k: "Moins de 100 000 €",
  "100k_200k": "100 000 € - 200 000 €",
  "200k_500k": "200 000 € - 500 000 €",
  "500k_1m": "500 000 € - 1 M€",
  plus_1m: "Plus de 1 M€",
};

const EXPERIENCE_LABELS = {
  salarie: "Salarié sans expérience de management",
  manager: "Manager / Chef d'équipe",
  cadre_dirigeant: "Cadre dirigeant",
  dirigeant_actuel: "Dirigeant actuel",
  ex_dirigeant: "Ex-dirigeant / Entrepreneur",
};

const HORIZON_LABELS = {
  moins_3mois: "Dans les 3 prochains mois",
  "3_6mois": "Dans 3 à 6 mois",
  "6_12mois": "Dans 6 à 12 mois",
  plus_12mois: "Dans plus de 12 mois (veille)",
};

const SECTEUR_LABELS = {
  industrie: "Industrie / Production",
  services: "Services aux entreprises",
  commerce: "Commerce / Distribution",
  artisanat: "Artisanat",
  tech: "Tech / Digital",
  sante: "Santé / Médical",
  batiment: "BTP / Bâtiment",
  restauration: "Restauration / Hôtellerie",
  tous: "Tous secteurs",
};

const ZONE_LABELS = {
  aura: "Auvergne-Rhône-Alpes",
  paca: "Provence-Alpes-Côte d'Azur",
  occitanie: "Occitanie",
  national: "France entière",
};

const ACCORD_LABELS = {
  oui: "Oui, j'ai un pré-accord",
  en_cours: "Discussions en cours",
  non: "Pas encore contacté",
};

const BUDGET_LABELS = {
  moins_200k: "Moins de 200 000 €",
  "200k_500k": "200 000 € - 500 000 €",
  "500k_1m": "500 000 € - 1 M€",
  "1m_3m": "1 M€ - 3 M€",
  plus_3m: "Plus de 3 M€",
};

const DISPONIBILITE_LABELS = {
  temps_plein: "Temps plein immédiat",
  transition: "Période de transition nécessaire",
  temps_partiel: "Temps partiel au début",
};

const ACCOMPAGNEMENT_LABELS = {
  autonome: "Autonome - Peu d'accompagnement",
  ponctuel: "Accompagnement ponctuel",
  complet: "Accompagnement complet souhaité",
};

const EFFECTIF_LABELS = {
  "0": "0 (solo)",
  "1_5": "1 - 5 salariés",
  "6_10": "6 - 10 salariés",
  "11_25": "11 - 25 salariés",
  "26_50": "26 - 50 salariés",
  "plus_50": "Plus de 50 salariés",
};

const ANCIENNETE_LABELS = {
  moins_5ans: "Moins de 5 ans",
  "5_10ans": "5 - 10 ans",
  "10_20ans": "10 - 20 ans",
  plus_20ans: "Plus de 20 ans",
};

const RESULTAT_NET_LABELS = {
  negatif: "Négatif / Perte",
  "0_50k": "0 - 50 000 €",
  "50k_100k": "50 000 € - 100 000 €",
  "100k_200k": "100 000 € - 200 000 €",
  "200k_500k": "200 000 € - 500 000 €",
  plus_500k: "Plus de 500 000 €",
};

const CHIFFRE_AFFAIRES_LABELS = {
  moins_100k: "Moins de 100 000 €",
  "100k_300k": "100 000 € - 300 000 €",
  "300k_500k": "300 000 € - 500 000 €",
  "500k_1m": "500 000 € - 1 M€",
  "1m_3m": "1 M€ - 3 M€",
  "3m_5m": "3 M€ - 5 M€",
  plus_5m: "Plus de 5 M€",
};

const NIVEAU_CONFIG = {
  premium: { color: "bg-accent", text: "Premium", icon: Trophy },
  qualifie: { color: "bg-primary", text: "Qualifié", icon: Star },
  en_construction: { color: "bg-orange-500", text: "En construction", icon: Clock },
  curieux: { color: "bg-gray-500", text: "Curieux", icon: AlertCircle },
};

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("access_token");
      const userType = localStorage.getItem("user_type");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        const endpoint = userType === "cedant" ? `${API}/cedants/me/profile` : `${API}/repreneurs/me/profile`;
        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setProfile(response.data);
      } catch (err) {
        console.error("Error fetching profile:", err);
        setError("Impossible de charger le profil.");
        toast.error("Erreur lors de la récupération du profil");
        if (err.response?.status === 401) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("user_type");
          navigate("/login");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
          <p className="text-xl font-medium text-primary">Chargement du profil...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl font-medium text-destructive mb-4">{error || "Profil non trouvé"}</p>
          <button 
            onClick={() => navigate("/")}
            className="text-accent font-bold hover:underline"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const userType = localStorage.getItem("user_type");
  const isCedant = userType === "cedant";
  const niveau = !isCedant ? (NIVEAU_CONFIG[profile.niveau] || NIVEAU_CONFIG.curieux) : null;
  const NiveauIcon = niveau?.icon;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-background py-16 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6">
          <div>
            <h1 className="text-4xl font-black text-primary mb-2">Mon Profil</h1>
            <p className="text-lg text-foreground/60 font-medium">
              Gérez vos informations {isCedant ? "d'entreprise" : "et suivez votre score de qualification"}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {!isCedant && (
              <Badge className={`${niveau.color} text-white px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2`}>
                <NiveauIcon className="w-4 h-4" />
                Profil {niveau.text}
              </Badge>
            )}
            {profile.validation ? (
              <Badge className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Validé
              </Badge>
            ) : (
              <Badge className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                <Clock className="w-4 h-4" />
                En attente de validation
              </Badge>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Info Sidebar */}
          <div className="space-y-8">
            <Card className="border-none shadow-sm overflow-hidden">
              <div className="h-2 bg-accent w-full" />
              <CardContent className="pt-8">
                <div className="flex flex-col items-center text-center mb-8">
                  <div className="w-24 h-24 bg-primary/5 rounded-full flex items-center justify-center mb-4">
                    <User className="w-12 h-12 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold text-primary capitalize">
                    {profile.prenom} {profile.nom}
                  </h2>
                  <p className="text-foreground/40 font-medium">{isCedant ? "Cédant" : "Repreneur"}</p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-foreground/70">
                    <Mail className="w-5 h-5 text-accent" />
                    <span className="font-medium truncate">{isCedant ? "••••@••••.••" : profile.email}</span>
                  </div>
                  <div className="flex items-center gap-3 text-foreground/70">
                    <Phone className="w-5 h-5 text-accent" />
                    <span className="font-medium">{isCedant ? "•• •• •• •• ••" : (profile.telephone || "Non renseigné")}</span>
                  </div>
                  <div className="flex items-center gap-3 text-foreground/70">
                    <Calendar className="w-5 h-5 text-accent" />
                    <span className="font-medium">Membre depuis {new Date(profile.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                  {isCedant && (
                    <div className="flex items-center gap-3 text-foreground/70">
                      <Briefcase className="w-5 h-5 text-accent" />
                      <span className="font-medium italic text-foreground/40">{profile.entreprise || "Dossier Confidentiel"}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {!isCedant && (
              <Card className="border-none shadow-sm">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-primary flex items-center gap-2">
                    <Trophy className="w-6 h-6 text-accent" />
                    Qualification
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-bold text-foreground/40 uppercase tracking-wider">Score Global</span>
                        <span className="text-sm font-bold text-accent">{profile.score}/100</span>
                      </div>
                      <Progress value={profile.score} className="h-2 bg-muted" />
                    </div>
                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                      <p className="text-sm text-primary font-medium leading-relaxed italic">
                        Votre score détermine votre priorité d'accès aux nouvelles opportunités sur la plateforme.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {isCedant && (
               <Card className="border-none shadow-sm">
               <CardHeader>
                 <CardTitle className="text-xl font-bold text-primary flex items-center gap-2">
                   <TrendingUp className="w-6 h-6 text-accent" />
                   Valorisation
                 </CardTitle>
               </CardHeader>
               <CardContent>
                 <div className="space-y-4">
                   <div className="p-4 bg-primary/5 rounded-lg border border-primary/10 text-center">
                     <p className="text-xs font-bold text-foreground/40 uppercase tracking-wider mb-1">Moyenne estimée</p>
                     <p className="text-2xl font-black text-accent">{formatCurrency(profile.valuation_estimee?.moyenne || 0)}</p>
                   </div>
                   <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-muted/50 rounded-lg text-center">
                        <p className="text-[10px] font-bold text-foreground/40 uppercase mb-1">Min</p>
                        <p className="text-sm font-bold text-primary">{formatCurrency(profile.valuation_estimee?.fourchette_basse || 0)}</p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded-lg text-center">
                        <p className="text-[10px] font-bold text-foreground/40 uppercase mb-1">Max</p>
                        <p className="text-sm font-bold text-primary">{formatCurrency(profile.valuation_estimee?.fourchette_haute || 0)}</p>
                      </div>
                   </div>
                 </div>
               </CardContent>
             </Card>
            )}
          </div>

          {/* Qualification Details */}
          <div className="md:col-span-2 space-y-8">
            <Card className="border-none shadow-sm">
              <CardHeader className="border-b border-border/40 pb-6">
                <CardTitle className="text-2xl font-bold text-primary">
                  {isCedant ? "Détails de l'entreprise" : "Détails du projet"}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-8">
                {isCedant ? (
                  <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
                    <DetailItem 
                      icon={Briefcase} 
                      label="Secteur" 
                      value={SECTEUR_LABELS[profile.valuation.secteur]} 
                    />
                    <DetailItem 
                      icon={Wallet} 
                      label="Chiffre d'affaires" 
                      value={CHIFFRE_AFFAIRES_LABELS[profile.valuation.chiffre_affaires]} 
                    />
                    <DetailItem 
                      icon={TrendingUp} 
                      label="Résultat net" 
                      value={RESULTAT_NET_LABELS[profile.valuation.resultat_net]} 
                    />
                    <DetailItem 
                      icon={TrendingUp} 
                      label="EBE annuel N-1" 
                      value={RESULTAT_NET_LABELS[profile.valuation.ebe_n1]} 
                    />
                    <DetailItem 
                      icon={Users} 
                      label="Effectif" 
                      value={EFFECTIF_LABELS[profile.valuation.effectif]} 
                    />
                    <DetailItem 
                      icon={Calendar} 
                      label="Ancienneté" 
                      value={ANCIENNETE_LABELS[profile.valuation.anciennete]} 
                    />
                    <DetailItem 
                      icon={MapPin} 
                      label="Localisation" 
                      value={ZONE_LABELS[profile.localisation]} 
                    />
                    <DetailItem 
                      icon={Clock} 
                      label="Horizon de cession" 
                      value={HORIZON_LABELS[profile.horizon_cession]} 
                    />
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
                    <DetailItem 
                      icon={Wallet} 
                      label="Capacité d'apport" 
                      value={CAPACITE_LABELS[profile.qualification.capacite_financiere]} 
                    />
                    <DetailItem 
                      icon={Briefcase} 
                      label="Expérience" 
                      value={EXPERIENCE_LABELS[profile.qualification.experience]} 
                    />
                    <DetailItem 
                      icon={Clock} 
                      label="Horizon de reprise" 
                      value={HORIZON_LABELS[profile.qualification.horizon_projet]} 
                    />
                    <DetailItem 
                      icon={CheckCircle2} 
                      label="Pré-accord bancaire" 
                      value={ACCORD_LABELS[profile.qualification.pre_accord_bancaire]} 
                    />
                    <DetailItem 
                      icon={Target} 
                      label="Budget maximum" 
                      value={BUDGET_LABELS[profile.qualification.budget_max] || "Non spécifié"} 
                    />
                    <DetailItem 
                      icon={AlertCircle} 
                      label="Disponibilité" 
                      value={DISPONIBILITE_LABELS[profile.qualification.disponibilite] || "Non spécifiée"} 
                    />
                    <DetailItem 
                      icon={Target} 
                      label="Accompagnement" 
                      value={ACCOMPAGNEMENT_LABELS[profile.qualification.accompagnement_souhaite] || "Non spécifié"} 
                    />
                  </div>
                )}

                <div className="mt-12 space-y-8 pt-8 border-t border-border/40">
                  {!isCedant && (
                    <>
                      <div className="space-y-4">
                        <h3 className="text-lg font-bold text-primary flex items-center gap-2">
                          <Target className="w-5 h-5 text-accent" />
                          Secteurs ciblés
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {profile.qualification.secteurs_cibles.map(s => (
                            <Badge key={s} variant="secondary" className="bg-muted text-primary font-semibold px-3 py-1">
                              {SECTEUR_LABELS[s] || s}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h3 className="text-lg font-bold text-primary flex items-center gap-2">
                          <MapPin className="w-5 h-5 text-accent" />
                          Zones géographiques
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {profile.qualification.zones_geographiques.map(z => (
                            <Badge key={z} variant="secondary" className="bg-muted text-primary font-semibold px-3 py-1">
                              {ZONE_LABELS[z] || z}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {(isCedant ? profile.motivation : profile.qualification.motivation) && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-bold text-primary">Motivation</h3>
                      <p className="text-foreground/70 leading-relaxed bg-muted/30 p-6 rounded-lg border border-border/40 italic">
                        "{isCedant ? profile.motivation : profile.qualification.motivation}"
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-accent" />
    </div>
    <div>
      <p className="text-xs font-bold text-foreground/40 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-base font-bold text-primary">{value || "Non renseigné"}</p>
    </div>
  </div>
);

export default Profile;
